<template>
  <div class="w-full p-4 md:p-6">
    <!-- Заголовок -->
    <div class="mb-4 md:mb-6 flex items-center justify-between gap-3">
      <div class="min-w-0">
        <h1 class="text-xl md:text-2xl font-bold text-neutral-900 tracking-tight">Уведомления</h1>
        <p class="text-neutral-400 text-xs md:text-sm mt-0.5 hidden sm:block">Важные события и обновления</p>
      </div>
      <div v-if="activeTab === 'current' && notifications.length > 0" class="flex gap-2 flex-shrink-0">
        <button
          @click="markAllAsRead"
          :disabled="markingAllAsRead"
          class="px-3 md:px-4 py-2 text-xs md:text-sm font-medium text-neutral-600 bg-neutral-100 rounded-xl hover:bg-neutral-200 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          <span class="hidden sm:inline">{{ markingAllAsRead ? 'Обработка...' : 'Прочитать все' }}</span>
          <span class="sm:hidden">{{ markingAllAsRead ? '...' : 'Прочитать' }}</span>
        </button>
      </div>
    </div>

    <!-- Мобильный фильтр: переключатель сайдбара -->
    <div class="md:hidden mb-3 flex items-center gap-2">
      <button
        @click="mobileFiltersOpen = !mobileFiltersOpen"
        class="flex-1 flex items-center justify-between px-4 py-2.5 text-sm font-medium text-neutral-700 bg-white border border-neutral-200 rounded-xl active:bg-neutral-50"
      >
        <span class="flex items-center gap-2">
          Фильтры
          <span v-if="activeTypeFilter || activeActorFilter" class="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-semibold">
            активны
          </span>
        </span>
        <svg :class="['w-4 h-4 text-neutral-400 transition-transform', mobileFiltersOpen ? 'rotate-180' : '']" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
        </svg>
      </button>
    </div>

    <!-- Баннер: нет разрешения браузера либо действующей подписки на сервере -->
    <div
      v-if="showPermissionBanner"
      class="mb-5 flex items-center justify-between gap-3 rounded-2xl bg-amber-50 border border-amber-100 px-5 py-4"
    >
      <div class="flex items-center gap-3">
        <div class="w-9 h-9 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
          <svg class="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        </div>
        <span class="text-sm text-amber-800">
          Браузерные уведомления отключены. Включите, чтобы не пропускать события.
        </span>
      </div>
      <div class="flex items-center gap-2 flex-shrink-0">
        <button
          v-if="permissionStatus !== 'denied'"
          @click="enablePushNotifications"
          class="px-3.5 py-1.5 text-xs font-semibold text-white bg-amber-500 rounded-lg hover:bg-amber-600 transition-all duration-200"
        >
          Включить
        </button>
        <span v-else class="text-xs text-amber-600">
          Разрешите в настройках браузера
        </span>
        <button
          @click="dismissPushBanner"
          class="p-1.5 text-amber-300 hover:text-amber-500 transition-colors rounded-lg hover:bg-amber-100"
          title="Скрыть"
        >
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Двухколоночный layout: уведомления + боковая панель фильтров -->
    <div class="flex flex-col md:flex-row gap-4 md:gap-6 items-stretch md:items-start">

      <!-- Левая колонка: табы + уведомления -->
      <div class="flex-1 min-w-0 order-2 md:order-1">

        <!-- Табы -->
        <div class="flex gap-1 bg-neutral-100 rounded-xl p-1 mb-5">
          <button
            @click="switchTab('current')"
            :class="[
              'flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200',
              activeTab === 'current' ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500 hover:text-neutral-700'
            ]"
          >
            Актуальное
            <span
              v-if="unreadCount > 0"
              class="ml-1.5 inline-flex items-center justify-center min-w-[20px] px-1.5 py-0.5 text-xs font-bold rounded-full bg-blue-500 text-white"
            >
              {{ unreadCount > 99 ? '99+' : unreadCount }}
            </span>
          </button>
          <button
            @click="switchTab('archive')"
            :class="[
              'flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200',
              activeTab === 'archive' ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500 hover:text-neutral-700'
            ]"
          >
            Архив
          </button>
        </div>

        <!-- Загрузка -->
        <div v-if="loading && notifications.length === 0" class="flex items-center justify-center py-20">
          <div class="flex flex-col items-center gap-3">
            <div class="w-8 h-8 border-2 border-neutral-200 border-t-neutral-600 rounded-full animate-spin"></div>
            <span class="text-sm text-neutral-400">Загрузка...</span>
          </div>
        </div>

        <!-- Заглушка - нет уведомлений -->
        <div v-else-if="notifications.length === 0 && !loading" class="flex flex-col items-center justify-center py-20 px-4">
          <div class="w-16 h-16 bg-neutral-100 rounded-2xl flex items-center justify-center mb-4">
            <svg class="w-8 h-8 text-neutral-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </div>
          <h3 class="text-base font-semibold text-neutral-700 mb-1">
            {{ activeTab === 'current' ? 'Нет новых уведомлений' : 'Архив пуст' }}
          </h3>
          <p class="text-sm text-neutral-400 text-center max-w-xs">
            {{ activeTab === 'current' ? 'Новые уведомления появятся здесь' : 'Прочитанные уведомления будут здесь' }}
          </p>
        </div>

        <!-- Список уведомлений (карточки, сгруппированные по датам) -->
        <div v-else class="space-y-6">
          <div v-for="group in groupedNotifications" :key="group.key">
            <!-- Заголовок группы (дата) -->
            <div class="flex items-center gap-3 mb-2.5 px-1">
              <div class="flex items-center gap-2">
                <svg class="w-4 h-4 text-neutral-300" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                </svg>
                <span class="text-xs font-semibold text-neutral-500 uppercase tracking-wider">{{ group.label }}</span>
              </div>
              <div class="flex-1 h-px bg-neutral-100"></div>
              <span class="text-xs text-neutral-300">{{ group.items.length }}</span>
            </div>

            <!-- Карточки уведомлений в группе -->
            <div class="space-y-2">
              <div
                v-for="item in group.items"
                :key="item.id"
                @click="handleNotificationClick(item)"
                :class="[
                  'group relative rounded-2xl border transition-all duration-200 cursor-pointer',
                  item.is_read
                    ? 'bg-white border-neutral-100 hover:border-neutral-200 hover:shadow-sm'
                    : 'bg-blue-50/40 border-blue-100 hover:border-blue-200 hover:shadow-sm'
                ]"
              >
                <div class="px-3 sm:px-5 py-3 sm:py-4">
                  <div class="flex items-start gap-2.5 sm:gap-3">
                    <!-- Иконка типа уведомления -->
                    <div :class="['w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5', getTypeIconClass(item)]">
                      <component :is="{ render: getTypeIconRender(item) }" />
                    </div>

                    <!-- Контент -->
                    <div class="min-w-0 flex-1">
                      <div class="flex items-center gap-2">
                        <div v-if="!item.is_read" class="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                        <h4 class="text-sm font-semibold text-neutral-900 truncate">{{ item.title }}</h4>
                      </div>
                      <p class="text-sm text-neutral-500 mt-0.5 line-clamp-2">{{ item.message }}</p>
                      <div class="flex items-center gap-3 mt-2.5 text-xs text-neutral-400">
                        <span v-if="getActorName(item) !== '—'" class="font-medium text-neutral-500">{{ getActorName(item) }}</span>
                        <span>{{ formatTime(item.created_at) }}</span>
                      </div>
                    </div>

                    <!-- Действия -->
                    <div class="flex items-center gap-0.5 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-200 flex-shrink-0">
                      <button
                        v-if="!item.is_read"
                        @click.stop="markAsRead(item.id)"
                        :disabled="markingAsRead === item.id"
                        class="p-2 rounded-lg hover:bg-neutral-100 transition-colors"
                        title="Прочитано"
                      >
                        <svg class="w-4 h-4 text-neutral-400 hover:text-blue-600" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                        </svg>
                      </button>
                      <button
                        v-else
                        @click.stop="markAsUnread(item.id)"
                        :disabled="markingAsUnread === item.id"
                        class="p-2 rounded-lg hover:bg-neutral-100 transition-colors"
                        title="Непрочитано"
                      >
                        <svg class="w-4 h-4 text-neutral-400 hover:text-blue-600" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3" />
                        </svg>
                      </button>
                      <button
                        @click.stop="deleteNotification(item.id)"
                        :disabled="deleting === item.id"
                        class="p-2 rounded-lg hover:bg-red-50 transition-colors"
                        title="Удалить"
                      >
                        <svg class="w-4 h-4 text-neutral-400 hover:text-red-500" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Пагинация -->
          <div v-if="pagination && pagination.last_page > 1" class="flex items-center justify-center gap-2 pt-4 pb-2">
            <button
              v-for="page in paginationPages"
              :key="page"
              @click="loadPage(page)"
              :disabled="page === pagination.current_page"
              :class="[
                'w-9 h-9 rounded-xl text-sm font-medium transition-all duration-200',
                page === pagination.current_page ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
              ]"
            >
              {{ page }}
            </button>
          </div>
        </div>
      </div><!-- /left column -->

      <!-- Правая боковая панель: фильтры -->
      <div
        :class="[
          'w-full md:w-64 flex-shrink-0 space-y-4 order-1 md:order-2 md:sticky md:top-4',
          mobileFiltersOpen ? '' : 'hidden md:block'
        ]"
      >
        <!-- Фильтр по участнику -->
        <div class="bg-white border border-neutral-100 rounded-2xl overflow-hidden">
          <div class="px-4 py-3 border-b border-neutral-100">
            <h3 class="text-xs font-semibold text-neutral-500 uppercase tracking-wider">Участник</h3>
          </div>
          <div class="px-3 py-3">
            <v-select
              v-model="activeActorFilter"
              :options="actors"
              label="name"
              :reduce="(a) => a.id"
              placeholder="Все участники"
              append-to-body
              @update:modelValue="handleActorFilterChange"
            >
              <template #no-options>Нет участников</template>
            </v-select>
          </div>
        </div>

        <!-- Фильтры по типам -->
        <div class="bg-white border border-neutral-100 rounded-2xl overflow-hidden">
          <div class="px-4 py-3 border-b border-neutral-100">
            <h3 class="text-xs font-semibold text-neutral-500 uppercase tracking-wider">Тип</h3>
          </div>
          <div class="py-1">
            <button
              @click="setTypeFilter(null)"
              :class="[
                'w-full flex items-center justify-between px-4 py-2.5 text-sm transition-all duration-150',
                activeTypeFilter === null ? 'bg-blue-50 text-blue-700 font-medium' : 'text-neutral-600 hover:bg-neutral-50'
              ]"
            >
              <span>Все</span>
              <span v-if="pagination" class="text-xs text-neutral-400 tabular-nums">{{ pagination.total }}</span>
            </button>
            <button
              v-for="ft in availableTypeFilters"
              :key="ft.type"
              @click="setTypeFilter(ft.type)"
              :class="[
                'w-full flex items-center justify-between px-4 py-2.5 text-sm transition-all duration-150',
                activeTypeFilter === ft.type ? 'bg-blue-50 text-blue-700 font-medium' : 'text-neutral-600 hover:bg-neutral-50'
              ]"
            >
              <span class="truncate">{{ ft.label }}</span>
              <span v-if="typeCounts[ft.type]" class="text-xs text-neutral-400 tabular-nums">{{ typeCounts[ft.type] }}</span>
            </button>
          </div>
        </div>
      </div><!-- /right sidebar -->
    </div>

    <!-- Модалка «Нет доступа к разделу» -->
    <div
      v-if="accessDeniedShow"
      class="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
    >
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="accessDeniedShow = false"></div>
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
        <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="sm:flex sm:items-start">
              <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-amber-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg class="h-6 w-6 text-amber-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 class="text-lg leading-6 font-medium text-gray-900">Нет доступа к разделу</h3>
                <div class="mt-2">
                  <p class="text-sm text-gray-500">У вас нет доступа к разделу, на который ведёт это уведомление. Обратитесь к администратору.</p>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              @click="accessDeniedShow = false"
              type="button"
              class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Понятно
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { h } from 'vue'
import { mapStores } from 'pinia'
import { useNotificationsStore } from '@/stores/notifications.js'
import { useAuthStore } from '@/stores/auth.js'
import { usePushNotifications } from '@/composables/usePushNotifications.js'

export default {
  name: 'Notifications',
  computed: {
    ...mapStores(useNotificationsStore, useAuthStore),

    /**
     * Группировка уведомлений по датам: Сегодня, Вчера, конкретные даты
     */
    groupedNotifications() {
      if (!this.notifications || this.notifications.length === 0) return []

      var groups = {}
      var order = []
      var now = new Date()
      var today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      var yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)

      for (var i = 0; i < this.notifications.length; i++) {
        var item = this.notifications[i]
        var d = new Date(item.created_at)
        var itemDate = new Date(d.getFullYear(), d.getMonth(), d.getDate())

        var key, label
        if (itemDate.getTime() === today.getTime()) {
          key = 'today'
          label = 'Сегодня'
        } else if (itemDate.getTime() === yesterday.getTime()) {
          key = 'yesterday'
          label = 'Вчера'
        } else {
          key = itemDate.toISOString().slice(0, 10)
          label = d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })
        }

        if (!groups[key]) {
          groups[key] = { key: key, label: label, items: [] }
          order.push(key)
        }
        groups[key].items.push(item)
      }

      var result = []
      for (var j = 0; j < order.length; j++) {
        result.push(groups[order[j]])
      }
      return result
    },

    /**
     * Доступные фильтры по типам (показываем только те, у которых есть count)
     */
    availableTypeFilters() {
      var self = this
      var allTypes = [
        { type: 'receipt_invoice_otk_control', label: 'Контроль ОТК' },
        { type: 'production_task_queued', label: 'Новые задания' },
        { type: 'production_task_done', label: 'Завершение заданий' },
      ]
      var result = []
      for (var i = 0; i < allTypes.length; i++) {
        if (self.typeCounts[allTypes[i].type]) {
          result.push(allTypes[i])
        }
      }
      return result
    },

    /**
     * Генерация массива страниц для пагинации (максимум 7 видимых)
     */
    paginationPages() {
      if (!this.pagination) return []
      const total = this.pagination.last_page
      const current = this.pagination.current_page
      const pages = []

      if (total <= 7) {
        for (let i = 1; i <= total; i++) pages.push(i)
      } else {
        pages.push(1)
        if (current > 3) pages.push('...')
        const start = Math.max(2, current - 1)
        const end = Math.min(total - 1, current + 1)
        for (let i = start; i <= end; i++) pages.push(i)
        if (current < total - 2) pages.push('...')
        pages.push(total)
      }
      return pages.filter((p) => typeof p === 'number')
    }
  },
  data() {
    return {
      activeTab: 'current',
      notifications: [],
      loading: false,
      pagination: null,
      unreadCount: 0,
      markingAsRead: null,
      markingAsUnread: null,
      markingAllAsRead: false,
      deleting: null,
      activeTypeFilter: null,
      typeCounts: {},
      activeActorFilter: null,
      actors: [],
      showPermissionBanner: false,
      permissionStatus: 'default',
      mobileFiltersOpen: false,
      accessDeniedShow: false
    }
  },
  watch: {
    'notificationsStore.incomingCounter'() {
      if (this.activeTab === 'current') {
        this.loadNotifications()
      } else {
        this.unreadCount = this.notificationsStore.unreadCount
      }
    }
  },
  async mounted() {
    this.checkPushPermission()
    await this.loadNotifications()
  },
  methods: {
    switchTab(tab) {
      if (this.activeTab === tab) return
      this.activeTab = tab
      this.activeTypeFilter = null
      this.activeActorFilter = null
      this.notifications = []
      this.pagination = null
      this.loadNotifications()
    },

    setTypeFilter(type) {
      if (this.activeTypeFilter === type) return
      this.activeTypeFilter = type
      this.notifications = []
      this.pagination = null
      this.loadNotifications()
    },

    handleActorFilterChange() {
      this.activeTypeFilter = null
      this.notifications = []
      this.pagination = null
      this.loadNotifications()
    },

    /**
     * CSS-класс фона иконки по типу уведомления
     */
    getTypeIconClass(notification) {
      switch (notification.type) {
        case 'receipt_invoice_otk_control':
        case 'receipt_invoice_otk_completed':
          return 'bg-amber-100'
        case 'production_task_queued':
          return 'bg-indigo-100'
        case 'production_operation_done':
        case 'production_task_done':
        case 'production_order_released':
          return 'bg-green-100'
        default:
          return 'bg-neutral-100'
      }
    },

    /**
     * Render-функция SVG-иконки по типу уведомления
     */
    getTypeIconRender(notification) {
      var iconColor, pathD
      switch (notification.type) {
        case 'receipt_invoice_otk_control':
        case 'receipt_invoice_otk_completed':
          iconColor = 'text-amber-600'
          pathD = 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
          break
        case 'production_task_queued':
          iconColor = 'text-indigo-600'
          pathD = 'M4 6h16M4 10h16M4 14h10M4 18h10'
          break
        case 'production_operation_done':
        case 'production_task_done':
        case 'production_order_released':
          iconColor = 'text-green-600'
          pathD = 'M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
          break
        default:
          iconColor = 'text-neutral-500'
          pathD = 'M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0'
          break
      }
      return function () {
        return h('svg', {
          class: 'w-5 h-5 ' + iconColor,
          fill: 'none',
          viewBox: '0 0 24 24',
          'stroke-width': '1.5',
          stroke: 'currentColor'
        }, [
          h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', d: pathD })
        ])
      }
    },

    /**
     * Имя автора (Фамилия Имя)
     */
    getActorName(notification) {
      if (!notification.actor) return '—'
      var firstName = notification.actor.first_name || ''
      var lastName = notification.actor.last_name || ''
      var fullName = (firstName + ' ' + lastName).trim()
      return fullName || notification.actor.name || '—'
    },

    async loadNotifications(page) {
      page = page || 1
      this.loading = true
      try {
        var params = { tab: this.activeTab, page: page, per_page: 20 }
        if (this.activeTypeFilter) params.type = this.activeTypeFilter
        if (this.activeActorFilter) params.actor_id = this.activeActorFilter

        var response = await axios.post('/api/notifications', params)

        if (response.data.success) {
          this.notifications = response.data.data
          this.pagination = response.data.pagination
          this.unreadCount = response.data.unread_count || 0
          this.typeCounts = response.data.type_counts || {}
          this.notificationsStore.unreadCount = this.unreadCount

          if (response.data.actors) {
            this.actors = response.data.actors.map(function (u) {
              var name = ((u.first_name || '') + ' ' + (u.last_name || '')).trim()
              return { id: u.id, name: name || u.name }
            })
          }
        }
      } catch (error) {
        console.error('Ошибка загрузки уведомлений:', error)
        this.$root.$toast?.error('Не удалось загрузить уведомления')
      } finally {
        this.loading = false
      }
    },

    async loadPage(page) {
      await this.loadNotifications(page)
    },

    async markAsRead(notificationId) {
      this.markingAsRead = notificationId
      try {
        var response = await axios.post('/api/notifications/' + notificationId + '/mark-as-read')
        if (response.data.success) {
          this.notifications = this.notifications.filter(function (n) { return n.id !== notificationId })
          this.unreadCount = Math.max(0, this.unreadCount - 1)
          this.notificationsStore.decrementCount()
          if (this.pagination) this.pagination.total = Math.max(0, this.pagination.total - 1)
          if (this.notifications.length === 0 && this.pagination && this.pagination.total > 0) {
            var page = Math.min(this.pagination.current_page, this.pagination.last_page)
            await this.loadNotifications(page)
          }
        }
      } catch (error) {
        console.error('Ошибка отметки:', error)
      } finally {
        this.markingAsRead = null
      }
    },

    async markAsUnread(notificationId) {
      this.markingAsUnread = notificationId
      try {
        var response = await axios.post('/api/notifications/' + notificationId + '/mark-as-unread')
        if (response.data.success) {
          this.notifications = this.notifications.filter(function (n) { return n.id !== notificationId })
          this.unreadCount += 1
          this.notificationsStore.incrementCount()
          if (this.pagination) this.pagination.total = Math.max(0, this.pagination.total - 1)
          if (this.notifications.length === 0 && this.pagination && this.pagination.total > 0) {
            var page = Math.min(this.pagination.current_page, this.pagination.last_page)
            await this.loadNotifications(page)
          }
        }
      } catch (error) {
        console.error('Ошибка отметки:', error)
      } finally {
        this.markingAsUnread = null
      }
    },

    async markAllAsRead() {
      this.markingAllAsRead = true
      try {
        var response = await axios.post('/api/notifications/mark-all-as-read')
        if (response.data.success) {
          this.unreadCount = 0
          this.notificationsStore.resetCount()
          await this.loadNotifications()
        }
      } catch (error) {
        console.error('Ошибка:', error)
      } finally {
        this.markingAllAsRead = false
      }
    },

    async deleteNotification(notificationId) {
      this.deleting = notificationId
      try {
        var response = await axios.post('/api/notifications/' + notificationId + '/delete')
        if (response.data.success) {
          var notification = this.notifications.find(function (n) { return n.id === notificationId })
          if (notification && !notification.is_read) {
            this.unreadCount = Math.max(0, this.unreadCount - 1)
            this.notificationsStore.decrementCount()
          }
          this.notifications = this.notifications.filter(function (n) { return n.id !== notificationId })
          if (this.pagination) this.pagination.total = Math.max(0, this.pagination.total - 1)
          if (this.notifications.length === 0 && this.pagination && this.pagination.total > 0) {
            var page = Math.min(this.pagination.current_page, this.pagination.last_page)
            await this.loadNotifications(page)
          }
        }
      } catch (error) {
        console.error('Ошибка удаления:', error)
      } finally {
        this.deleting = null
      }
    },

    /**
     * Клик по уведомлению — переход к сущности
     */
    async handleNotificationClick(notification) {
      if (!notification.is_read) {
        await this.markAsRead(notification.id)
      }

      var modules = this.authStore?.currentUser?.modules || []

      if (notification.entity_type === 'receipt_invoice' && notification.entity_id) {
        var otkModule = modules.find(function (m) {
          return m.key === 'storehouse' && (m.name || '').toLowerCase() === 'склад отк'
        })
        // Нет доступа к складу ОТК — не редиректим, показываем модалку
        if (!otkModule) {
          this.accessDeniedShow = true
          return
        }
        this.$router.push({
          name: 'OtkControlPage',
          params: { id: otkModule.id, receiptInvoiceId: notification.entity_id }
        })
      } else if (notification.entity_type === 'production_order' && notification.entity_id) {
        // Нет доступа к модулю производства — модалка
        if (!modules.some(function (m) { return m.key === 'production' })) {
          this.accessDeniedShow = true
          return
        }
        var orderUrl = '/production/orders/' + notification.entity_id
        if (notification.data && notification.data.task_id) {
          orderUrl += '?taskId=' + notification.data.task_id
        } else if (notification.data && notification.data.operation_id) {
          orderUrl += '?operationId=' + notification.data.operation_id
        }
        // Строкой, а не { path }: vue-router при объектной форме отбрасывает query,
        // и заказ открылся бы без прокрутки к нужному заданию или операции
        this.$router.push(orderUrl)
      } else if (notification.entity_type === 'production_desk') {
        // Нет доступа к модулю рабочего стола (или стол не привязан к модулю) — модалка
        var deskModule = notification.entity_id
          ? modules.find(function (m) { return m.id === notification.entity_id })
          : null
        if (!deskModule) {
          this.accessDeniedShow = true
          return
        }
        var deskLink = notification.data && notification.data.link
        if (deskLink) {
          this.$router.push(deskLink)
        }
      } else if (notification.entity_type === 'smk_task') {
        // Нет доступа к рабочему столу СМК — модалка
        var smkModule = modules.find(function (m) { return m.key === 'smk-desk' })
        if (!smkModule) {
          this.accessDeniedShow = true
          return
        }
        // Путь всегда собираем по СВОЕМУ модулю СМК: ссылка в уведомлении построена бэкендом
        // по первому столу СМК и при нескольких столах увела бы на чужой модуль
        var smkLink = notification.entity_id
          ? '/smk-desk/' + smkModule.id + '?tab=tasks&taskId=' + notification.entity_id
          : (notification.data && notification.data.link)
        if (!smkLink) {
          this.accessDeniedShow = true
          return
        }
        // Строкой, а не { path }: иначе vue-router отбрасывает query и стол откроется без задания
        this.$router.push(smkLink)
      } else if (notification.entity_type === 'controlled_use') {
        // КИ доступен всем, кому доступен рабочий стол СМК — переход по ссылке из data
        var cuLink = notification.data && notification.data.link
        if (cuLink) {
          this.$router.push(cuLink)
        }
      } else if (notification.entity_type === 'deviation_permit') {
        // РнО доступен всем, кому доступен рабочий стол СМК — переход по ссылке из data
        var dpLink = notification.data && notification.data.link
        if (dpLink) {
          this.$router.push(dpLink)
        }
      } else if (notification.entity_type === 'risk_assessment') {
        // ОР доступен всем, кому доступен рабочий стол СМК — переход по ссылке из data
        var raLink = notification.data && notification.data.link
        if (raLink) {
          this.$router.push(raLink)
        }
      } else if (notification.entity_type === 'nc_investigation') {
        // РН доступен всем, кому доступен рабочий стол СМК — переход по ссылке из data
        var ncLink = notification.data && notification.data.link
        if (ncLink) {
          this.$router.push(ncLink)
        }
      } else if (notification.entity_type === 'product_correction') {
        // Кр доступен всем, кому доступен рабочий стол СМК — переход по ссылке из data
        var pcLink = notification.data && notification.data.link
        if (pcLink) {
          this.$router.push(pcLink)
        }
      } else if (notification.entity_type === 'nonconformity_case') {
        // Нет доступа к рабочему столу СМК — модалка
        var ncDeskModule = modules.find(function (m) { return m.key === 'smk-desk' })
        if (!ncDeskModule) {
          this.accessDeniedShow = true
          return
        }
        // Путь собираем по СВОЕМУ модулю СМК: ссылка в уведомлении построена бэкендом
        // по первому столу СМК и при нескольких столах увела бы на чужой модуль.
        // Ключ вкладки реестра приходит с бэкенда (data.desk_tab) — второй копии маршрута нет
        var ncCaseTab = (notification.data && notification.data.desk_tab) || 'nonconformity'
        var ncCaseLink = notification.entity_id
          ? '/smk-desk/' + ncDeskModule.id + '?tab=' + ncCaseTab + '&caseId=' + notification.entity_id
          : (notification.data && notification.data.link)
        if (!ncCaseLink) {
          this.accessDeniedShow = true
          return
        }
        // Строкой, а не { path }: иначе vue-router отбросит query и реестр
        // откроется без нужной строки
        this.$router.push(ncCaseLink)
      }
    },

    async checkPushPermission() {
      var pushNotifications = usePushNotifications()
      this.permissionStatus = pushNotifications.getPermissionStatus()
      if (this.permissionStatus === 'unsupported') return

      // Разрешение браузера ещё не значит, что уведомления дойдут: подписка могла
      // протухнуть и быть удалена сервером либо выдана в другом браузере
      var hasSubscription = await this.loadPushSubscriptionStatus()
      if (this.permissionStatus === 'granted' && hasSubscription) return

      if (document.cookie.includes('push_banner_dismissed')) return
      this.showPermissionBanner = true
    },

    /**
     * Есть ли у текущего сотрудника действующая подписка на сервере
     */
    async loadPushSubscriptionStatus() {
      try {
        var response = await axios.get('/api/push-subscriptions/status')
        return response.data.has_subscription === true
      } catch (error) {
        // Статус недоступен — не пугаем баннером того, кто разрешение уже дал
        console.error('Ошибка получения статуса push-подписки:', error)
        return true
      }
    },

    async enablePushNotifications() {
      var pushNotifications = usePushNotifications()
      var result = await pushNotifications.requestPermissionAndSubscribe()
      this.permissionStatus = result.permission
      if (result.success) {
        this.showPermissionBanner = false
        return
      }
      if (result.message) this.$root.$toast?.error(result.message)
    },

    dismissPushBanner() {
      this.showPermissionBanner = false
      document.cookie = 'push_banner_dismissed=1; max-age=1209600; path=/'
    },

    formatTime(date) {
      if (!date) return ''
      var d = new Date(date)
      var now = new Date()
      var diff = now - d
      var minutes = Math.floor(diff / 60000)
      var hours = Math.floor(diff / 3600000)
      var days = Math.floor(diff / 86400000)

      if (minutes < 1) return 'только что'
      if (minutes < 60) return minutes + 'м назад'
      if (hours < 24) return hours + 'ч назад'
      if (days < 7) return days + 'д назад'
      return d.toLocaleDateString('ru-RU')
    }
  }
}
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
